import React, { useState, useEffect, useCallback } from "react";
import { Search, Trash2, AlertTriangle, User } from "lucide-react";
import Modal from "./Modal";
import { deleteUser } from "../../utils/deleteUser";
import { getUserByEmail } from "../../utils/getUserByEmail";
import { toast } from "react-toastify";
import supabase from "../../utils/supabase";
import useUserStore from "../../stores/user";
import { useT } from "../../stores/uiLang";
import { displayEmail } from "../../utils/kidLogin";

// Run E3 B4: this modal follows the UI language (EN text unchanged).
const DS_T = {
  en: {
    delStudent: "Delete Student",
    student: "Student",
    email: "Email Address",
    enrolled: "Enrolled Class:",
    unknown: "Unknown",
    failClasses: "Failed to fetch classes",
    failSearch: "Failed to search for student",
    failDeleteWith: "Failed to delete student: ",
    deleted: "Student deleted successfully",
    failDelete: "Failed to delete student",
    sure: "Are you sure you want to delete this student?",
    body: "This action cannot be undone. The student will be permanently removed from the system and all their data will be lost.",
    back: "Back to List",
    deleting: "Deleting...",
    enterEmail: "Enter Student Email",
    emailPh: "Enter student email address...",
    search: "Search",
    searching: "Searching for student...",
    notFound: "Student not found",
    notFoundBody: "No student found with this email address in your classes",
    searchFor: "Search for a student",
    searchForBody: "Enter a student's email address to find and delete them",
    cancel: "Cancel",
    noEmail: "No email (class code login)",
  },
  es: {
    delStudent: "Eliminar estudiante",
    student: "Estudiante",
    email: "Correo electrónico",
    enrolled: "Clase en la que está inscrito:",
    unknown: "Desconocida",
    failClasses: "No se pudieron cargar las clases",
    failSearch: "No se pudo buscar al estudiante",
    failDeleteWith: "No se pudo eliminar al estudiante: ",
    deleted: "El estudiante se eliminó",
    failDelete: "No se pudo eliminar al estudiante",
    sure: "¿Seguro que quieres eliminar a este estudiante?",
    body: "No se puede deshacer. El estudiante se borrará para siempre del sistema y se perderán todos sus datos.",
    back: "Volver a la lista",
    deleting: "Eliminando...",
    enterEmail: "Escribe el correo electrónico del estudiante",
    emailPh: "Escribe el correo electrónico del estudiante...",
    search: "Buscar",
    searching: "Buscando al estudiante...",
    notFound: "No se encontró al estudiante",
    notFoundBody: "No hay ningún estudiante con este correo electrónico en tus clases",
    searchFor: "Busca a un estudiante",
    searchForBody: "Escribe el correo electrónico del estudiante para encontrarlo y eliminarlo",
    cancel: "Cancelar",
    noEmail: "Sin correo (entra con código de clase)",
  },
};

interface Student {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  className?: string;
  authUserId?: string;
}

const StudentDisplay: React.FC<{
  student: Student;
  showDeleteButton?: boolean;
  onDeleteClick?: () => void;
  deleteButtonText?: string;
}> = ({
  student,
  showDeleteButton = false,
  onDeleteClick,
  deleteButtonText,
}) => {
  const t = useT(DS_T);
  return (
    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900">
                {student.firstname} {student.lastname}
              </h4>
              <p className="text-sm text-gray-500">{t.student}</p>
            </div>
          </div>
          {showDeleteButton && onDeleteClick && (
            <button
              onClick={onDeleteClick}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deleteButtonText ?? t.delStudent}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div className="bg-white p-4 rounded-md border border-gray-200">
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-700">
                {t.email}
              </span>
            </div>
            <p className="text-sm text-gray-900 font-mono">{displayEmail(student.email, t.noEmail)}</p>
          </div>

          <div className="bg-white p-4 rounded-md border border-gray-200">
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-700">
                {t.enrolled}
              </span>
            </div>
            <p className="text-sm text-gray-900">{student.className}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface DeleteStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStudentDeleted: () => void;
}

const DeleteStudentModal: React.FC<DeleteStudentModalProps> = ({
  isOpen,
  onClose,
  onStudentDeleted,
}) => {
  const t = useT(DS_T);
  const [emailInput, setEmailInput] = useState("");
  const [foundStudent, setFoundStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const { user } = useUserStore();

  const searchStudentByEmail = useCallback(
    async (email: string) => {
      if (!user || !email.trim()) {
        setFoundStudent(null);
        return;
      }

      setLoading(true);
      setHasSearched(true);
      try {
        const { data: classes, error: classesError } = await supabase
          .from("classes")
          .select("id")
          .eq("user_id", user.id);

        if (classesError) {
          toast.error(t.failClasses);
          return;
        }

        if (!classes || classes.length === 0) {
          setFoundStudent(null);
          return;
        }

        const classIds = classes.map((c) => c.id);

        const { user: userData, error: userError } = await getUserByEmail(
          email
        );

        if (userError || !userData) {
          setFoundStudent(null);
          return;
        }

        const userId = userData.id;

        // Run all database queries in parallel
        const [
          { data: classStudents, error: classStudentsError },
          { data: studentProfile, error: profilesError },
        ] = await Promise.all([
          supabase
            .from("class_students")
            .select("user_id, class_id")
            .eq("user_id", userId)
            .in("class_id", classIds),
          supabase
            .from("profiles")
            .select("id, firstname, lastname")
            .eq("id", userId)
            .eq("role", "student")
            .single(),
        ]);

        if (
          classStudentsError ||
          !classStudents ||
          classStudents.length === 0 ||
          profilesError ||
          !studentProfile
        ) {
          setFoundStudent(null);
          return;
        }

        // Get class names after we know the student is valid
        const { data: classNames } = await supabase
          .from("classes")
          .select("name")
          .in(
            "id",
            classStudents.map((cs) => cs.class_id)
          );

        const student = {
          ...studentProfile,
          email: userData.email,
          className: classNames?.map((c) => c.name).join(", ") || t.unknown,
          authUserId: userId,
        };

        setFoundStudent(student);
      } catch (error) {
        console.error("Error searching for student:", error);
        toast.error(t.failSearch);
        setFoundStudent(null);
      } finally {
        setLoading(false);
      }
    },
    [user, t]
  );

  useEffect(() => {
    if (isOpen) {
      setEmailInput("");
      setFoundStudent(null);
      setSelectedStudent(null);
      setHasSearched(false);
    }
  }, [isOpen]);

  const handleDelete = async () => {
    if (!selectedStudent) return;

    setIsDeleting(true);
    try {
      const userIdToDelete = selectedStudent.authUserId || selectedStudent.id;
      const { error } = await deleteUser(userIdToDelete);
      if (error) {
        toast.error(t.failDeleteWith + error);
        return;
      }

      toast.success(t.deleted);
      onStudentDeleted();
      setSelectedStudent(null);
      setFoundStudent(null);
      setEmailInput("");
    } catch (error) {
      console.error("Error deleting student:", error);
      toast.error(t.failDelete);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStudentSelect = () => {
    if (foundStudent) {
      setSelectedStudent(foundStudent);
    }
  };

  const handleBackToList = () => {
    setSelectedStudent(null);
  };

  if (!isOpen) return null;

  if (selectedStudent) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={t.delStudent}>
        <div className="space-y-4">
          <div className="flex items-center justify-center">
            <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>

          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t.sure}
            </h3>
            <p className="text-sm text-gray-600">
              {t.body}
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={handleBackToList}
              disabled={isDeleting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t.back}
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t.deleting}
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t.delStudent}
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t.delStudent}>
      <div className="space-y-4">
        <div>
          <label
            htmlFor="email-search"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {t.enterEmail}
          </label>
          <div className="flex space-x-2">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="email-search"
                type="email"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t.emailPh}
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyPress={(e) => {
                  if (
                    e.key === "Enter" &&
                    emailInput.trim() &&
                    emailInput.includes("@") &&
                    emailInput.includes(".")
                  ) {
                    searchStudentByEmail(emailInput);
                  }
                }}
              />
            </div>
            <button
              onClick={() => searchStudentByEmail(emailInput)}
              disabled={
                !emailInput.trim() ||
                !emailInput.includes("@") ||
                !emailInput.includes(".") ||
                loading
              }
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
              ) : (
                t.search
              )}
            </button>
          </div>
        </div>

        <div className="min-h-[200px]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-2 text-gray-500">
                {t.searching}
              </span>
            </div>
          ) : foundStudent ? (
            <StudentDisplay
              student={foundStudent}
              showDeleteButton={true}
              onDeleteClick={handleStudentSelect}
            />
          ) : hasSearched ? (
            <div className="text-center py-8">
              <User className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {t.notFound}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t.notFoundBody}
              </p>
            </div>
          ) : (
            <div className="text-center py-8">
              <Search className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {t.searchFor}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t.searchForBody}
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {t.cancel}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteStudentModal;
